import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Everything the Today screen shows comes from the caller's own tenant data
// (RLS-scoped); nothing here is mocked. Each query is independent so one
// failing/unauthorised table never blanks the whole screen.

const OPEN_APPROVAL_STATUSES = ['pending_review', 'escalated', 'more_evidence_requested'] as const;

export interface TodayEmail { id: string; subject: string | null; sender_name: string | null; received_at: string | null; escalation_flag: boolean | null; }
export interface TodayMeeting { id: string; title: string | null; start_time: string | null; ai_summary: string | null; ai_action_items: Array<{ owner?: string; action?: string }> | null; }
export interface TodayRisk { id: string; entity_type: string; entity_id: string; rule_id: string; severity: string; detected_at: string; }
export interface TodayProject { id: string; external_id: string | null; name: string; health: string | null; progress: number | null; }
export interface TodayTask { id: string; external_id: string | null; title: string; project_external_id: string | null; status: string | null; due_date: string | null; }
export interface TodayApproval { id: string; title: string; summary: string | null; status: string; severity: string | null; created_at: string; }
export interface TodayDecision { id: string; title: string; project_name: string | null; due_date: string | null; priority: string | null; }

const useTodayQuery = <T,>(key: string, fn: () => PromiseLike<{ data: T | null; error: { message: string } | null }>, fallback: T) =>
  useQuery({
    queryKey: ['today', key],
    staleTime: 60_000,
    queryFn: async (): Promise<T> => {
      const { data, error } = await fn();
      if (error) throw new Error(error.message);
      return data ?? fallback;
    },
  });

export function useTodayData() {
  const unread = useQuery({
    queryKey: ['today', 'unreadCount'],
    staleTime: 60_000,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('emails_sync').select('id', { count: 'exact', head: true })
        .eq('is_read', false).or('direction.eq.inbound,direction.is.null');
      if (error) throw new Error(error.message);
      return count ?? 0;
    },
  });
  const emails = useTodayQuery<TodayEmail[]>('emails', () =>
    supabase.from('emails_sync').select('id,subject,sender_name,received_at,escalation_flag')
      .eq('is_read', false).or('direction.eq.inbound,direction.is.null')
      .order('escalation_flag', { ascending: false, nullsFirst: false }).order('received_at', { ascending: false }).limit(5), []);
  const meetings = useTodayQuery<TodayMeeting[]>('meetings', () =>
    supabase.from('meetings_sync').select('id,title,start_time,ai_summary,ai_action_items')
      .order('start_time', { ascending: false }).limit(3) as unknown as PromiseLike<{ data: TodayMeeting[] | null; error: { message: string } | null }>, []);
  const risks = useTodayQuery<TodayRisk[]>('risks', () =>
    supabase.from('risk_signals').select('id,entity_type,entity_id,rule_id,severity,detected_at')
      .is('resolved_at', null).order('detected_at', { ascending: false }).limit(20), []);
  const projects = useTodayQuery<TodayProject[]>('projects', () =>
    supabase.from('projects_sync').select('id,external_id,name,health,progress').order('name'), []);
  const tasks = useTodayQuery<TodayTask[]>('tasks', () =>
    supabase.from('tasks_sync').select('id,external_id,title,project_external_id,status,due_date')
      .neq('status', 'done').order('due_date', { ascending: true }).limit(200), []);
  const approvals = useTodayQuery<TodayApproval[]>('approvals', () =>
    supabase.from('ai_outputs').select('id,title,summary,status,severity,created_at')
      .in('status', [...OPEN_APPROVAL_STATUSES]).order('created_at', { ascending: false }).limit(20) as unknown as PromiseLike<{ data: TodayApproval[] | null; error: { message: string } | null }>, []);
  const decisions = useTodayQuery<TodayDecision[]>('decisions', () =>
    supabase.from('decisions').select('id,title,project_name,due_date,priority')
      .eq('status', 'pending').order('due_date', { ascending: true }).limit(10), []);

  const all = [unread, emails, meetings, risks, projects, tasks, approvals, decisions];
  return {
    unreadCount: unread.data ?? 0,
    emails: emails.data ?? [], meetings: meetings.data ?? [], risks: risks.data ?? [],
    projects: projects.data ?? [], tasks: tasks.data ?? [], approvals: approvals.data ?? [], decisions: decisions.data ?? [],
    isLoading: all.some((x) => x.isLoading),
    failed: all.filter((x) => x.isError).length,
  };
}
